import type { Practitioner } from '@medplum/fhirtypes';
import {
  getAcceptsInsurance,
  getClinical,
  getSessionFormats,
} from '@unify/fhir-model/resources/practitioner';

export interface ReferralPreferenceDefaults {
  acceptsInPerson: boolean;
  noMinors: boolean;
  noCouplesFamilies: boolean;
  onlyCashPay: boolean;
}

const MINOR_AGE_RE = /child|adolescent|teen|minor/;
const COUPLES_FAMILY_RE = /couple|family/;

/** Map a provider's Clinical Info / intake flags onto referral-request checkboxes. */
export function referralPreferencesFromPractitioner(
  practitioner: Practitioner | undefined,
): ReferralPreferenceDefaults {
  if (!practitioner) {
    return {
      acceptsInPerson: false,
      noMinors: false,
      noCouplesFamilies: false,
      onlyCashPay: false,
    };
  }

  const formats = (getSessionFormats(practitioner) ?? '').toLowerCase();
  const clinical = getClinical(practitioner);
  const ageGroups = (clinical?.ageGroups ?? []).map((value) => value.toLowerCase());
  const clinicalText = [
    ...(clinical?.specialties ?? []),
    ...(clinical?.modalities ?? []),
  ].join(' ').toLowerCase();

  const treatsMinors = ageGroups.some((age) => MINOR_AGE_RE.test(age));
  const treatsCouplesOrFamilies = COUPLES_FAMILY_RE.test(clinicalText);

  return {
    acceptsInPerson: formats.includes('in-person') || formats.includes('in person'),
    noMinors: ageGroups.length > 0 ? !treatsMinors : false,
    noCouplesFamilies:
      (clinical?.specialties?.length || clinical?.modalities?.length)
        ? !treatsCouplesOrFamilies
        : false,
    onlyCashPay: getAcceptsInsurance(practitioner) === false,
  };
}
